import type * as THREE from 'three';
import { CALENDAR, PLAYABLE_CLASSES, type HeroClass } from '@somnhold/content';
import { waveHoursValid } from '@somnhold/protocol';
import { api, ApiError } from './campaign/server.ts';
import { getLang, t } from './i18n.ts';
import { clear, h } from './ui/dom.ts';
import { ICONS } from './ui/icons.ts';
import { heroKey } from '@somnhold/render';
import { thumb } from './ui/thumbs.ts';

export interface Me {
  user: { id: string; email: string; displayName: string; lang: 'es' | 'en' };
  campaigns: { id: string; name: string; status: string; heroClass: HeroClass; players: number; mine?: number }[];
}

/** null = signed out; undefined = server unreachable (static hosting, offline). */
export const fetchMe = async (): Promise<Me | null | undefined> => {
  try {
    const me = await api<Me>('/api/me', { timeoutMs: 4000 });
    // Anything that isn't our account payload means there's no Somnhold server behind /api.
    return me && typeof me === 'object' && me.user && Array.isArray(me.campaigns) ? me : undefined;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) return null;
    return undefined;
  }
};

const appEl = (): HTMLElement => document.getElementById('app') as HTMLElement;

export const toastGlobal = (msg: string): void => {
  let el = document.getElementById('global-toast');
  if (!el) {
    el = h('div#global-toast.toast', { style: 'z-index:60', onclick: () => el?.classList.remove('show') });
    document.body.append(el);
  }
  el.textContent = msg;
  el.classList.add('show');
  window.setTimeout(() => el?.classList.remove('show'), 2600);
};

export const modal = (title: string, sub: string, body: HTMLElement, actions: HTMLElement[]): HTMLElement => {
  const back = h('div.modal-back');
  back.append(h('div.modal', { role: 'dialog', 'aria-modal': 'true' }, h('header', {}, h('h2', {}, title), sub ? h('p', {}, sub) : ''), h('div.body', {}, body), h('footer', {}, ...actions)));
  appEl().append(back);
  return back;
};

export const signInModal = (next = '/'): void => {
  const email = h('input.text-input', { type: 'email', autocomplete: 'email', inputmode: 'email', placeholder: 'nombre@correo.com', 'aria-label': t('auth.email'), 'data-test': 'email' }) as HTMLInputElement;
  const status = h('p.hint', { style: 'margin:0;font-family:var(--font-serif);color:var(--text-soft)' });
  const send = h('button.btn', { 'data-test': 'send-link' }, t('auth.send')) as HTMLButtonElement;
  const body = h('div', { style: 'display:grid;gap:10px' }, h('div.field', {}, h('label', {}, t('auth.email')), email), status);
  const back = modal(t('auth.title'), t('auth.hint'), body, [h('button.btn.ghost', { onclick: () => back.remove() }, t('menu.back')), send]);
  const go = async (): Promise<void> => {
    if (!email.value.includes('@')) {
      email.focus();
      return;
    }
    send.disabled = true;
    try {
      const r = await api<{ ok: true; devLink?: string }>('/api/auth/magic', { body: { email: email.value, lang: getLang(), next } });
      clear(status);
      status.append(t('auth.sent'));
      if (r.devLink) {
        // Dev mode only: the link points at the API origin; open it through this origin's proxy.
        const u = new URL(r.devLink);
        status.append(h('br'), h('a.link', { href: `${u.pathname}${u.search}`, 'data-test': 'dev-link' }, t('auth.devLink')));
      }
    } catch (e) {
      status.textContent = e instanceof ApiError && e.code === 'rate' ? t('auth.rate') : t('net.error');
      send.disabled = false;
    }
  };
  send.addEventListener('click', () => void go());
  email.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') void go();
  });
  window.setTimeout(() => email.focus(), 50);
};

export const classPicker = (r: THREE.WebGLRenderer | null, initial: HeroClass, onPick: (c: HeroClass) => void, taken: string[] = []): HTMLElement => {
  const grid = h('div.class-pick');
  // Phones hide the descriptions inside the tiles and show the picked one here instead.
  const desc = h('p.class-desc');
  const say = (c: HeroClass): void => {
    desc.textContent = taken.includes(c) ? t('invite.taken') : t(`class.${c}.desc`);
  };
  say(initial);
  const wrap = h('div.class-picker', {}, grid, desc);
  const tiles: HTMLElement[] = [];
  for (const cls of PLAYABLE_CLASSES) {
    const tile = h(
      `button.tile${cls === initial ? '.current' : ''}`,
      { type: 'button', 'data-class': cls },
      r ? h('img', { src: thumb(r, heroKey(cls), 104), alt: '' }) : h('span.glyph', { html: ICONS.keep }),
      h('span', {}, h('h3', {}, t(`class.${cls}`)), h('p', {}, taken.includes(cls) ? t('invite.taken') : t(`class.${cls}.desc`))),
    );
    tile.addEventListener('click', () => {
      for (const x of tiles) x.classList.remove('current');
      tile.classList.add('current');
      say(cls);
      onPick(cls);
    });
    tiles.push(tile);
    grid.append(tile);
  }
  return wrap;
};

const hourSelect = (value: number): HTMLSelectElement => {
  const sel = h('select.text-input') as HTMLSelectElement;
  for (let i = 0; i < 24; i++) sel.append(h('option', { value: i, ...(i === value ? { selected: true } : {}) }, `${String(i).padStart(2, '0')}:00`));
  return sel;
};

export const createCampaignModal = (r: THREE.WebGLRenderer | null, me: Me, onCreated: (id: string) => void): void => {
  let cls: HeroClass = 'warrior';
  const name = h('input.text-input', { type: 'text', maxlength: 40, value: getLang() === 'es' ? 'Los guardianes del lomo' : 'Wardens of the Shell', 'data-test': 'camp-name' }) as HTMLInputElement;
  const display = h('input.text-input', { type: 'text', maxlength: 24, value: me.user.displayName }) as HTMLInputElement;
  const tz = h('select.text-input') as HTMLSelectElement;
  const here = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const zones = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [here];
  for (const z of zones.includes(here) ? zones : [here, ...zones]) tz.append(h('option', { value: z, ...(z === here ? { selected: true } : {}) }, z.replace(/_/g, ' ')));
  const [h1, h2, h3] = CALENDAR.defaultWaveHours.map(hourSelect) as [HTMLSelectElement, HTMLSelectElement, HTMLSelectElement];
  const hoursBad = h('span.small.warn', { hidden: true, role: 'alert', 'data-test': 'hours-bad' }, t('campaigns.hoursBad', { n: CALENDAR.minWaveGapHours }));
  // Depth as two explained cards: what changes is the kind of nightmare that climbs the titan.
  const depth = { value: 'light' as 'light' | 'deep' };
  const depthPick = h('div.depth-pick', { role: 'radiogroup', 'aria-label': t('campaigns.depth') });
  for (const d of ['light', 'deep'] as const) {
    const card = h(
      `button.depth-card${d === depth.value ? '.on' : ''}`,
      { type: 'button', role: 'radio', 'aria-checked': String(d === depth.value), 'data-depth': d },
      h('b', {}, t(`campaigns.depth.${d}`)),
      h('span', {}, t(`campaigns.depth.${d}.desc`)),
    );
    card.addEventListener('click', () => {
      depth.value = d;
      for (const c of depthPick.children) {
        const on = (c as HTMLElement).dataset.depth === d;
        c.classList.toggle('on', on);
        c.setAttribute('aria-checked', String(on));
      }
    });
    depthPick.append(card);
  }
  // The bastion: the classic ring, or from scratch on terraced land (docs/CONSTRUCCION.md).
  const land = { value: false };
  const landPick = h('div.depth-pick', { role: 'radiogroup', 'aria-label': t('campaigns.bastion') });
  for (const on of [false, true]) {
    const card = h(
      `button.depth-card${on === land.value ? '.on' : ''}`,
      { type: 'button', role: 'radio', 'aria-checked': String(on === land.value), 'data-land': String(on) },
      h('b', {}, t(on ? 'campaigns.land' : 'campaigns.ring')),
      h('span', {}, t(on ? 'campaigns.land.desc' : 'campaigns.ring.desc')),
    );
    card.addEventListener('click', () => {
      land.value = on;
      for (const c of landPick.children) {
        const sel = (c as HTMLElement).dataset.land === String(on);
        c.classList.toggle('on', sel);
        c.setAttribute('aria-checked', String(sel));
      }
    });
    landPick.append(card);
  }
  const body = h(
    'div',
    { style: 'display:grid;gap:12px' },
    h('div.field', {}, h('label', {}, t('campaigns.name')), name),
    h('div.field', {}, h('label', {}, t('menu.yourName')), display),
    h('div.field', {}, h('span.lbl', {}, t('menu.pickClass')), classPicker(r, cls, (c) => (cls = c))),
    h('div.field', {}, h('label', {}, t('campaigns.timezone')), tz),
    h('div.field', {}, h('span.lbl', {}, t('campaigns.hours')), h('div', { style: 'display:grid;grid-template-columns:repeat(3,1fr);gap:8px' }, h1, h2, h3), h('span.small', {}, t('campaigns.hoursHint')), hoursBad),
    h('div.field', {}, h('span.lbl', {}, t('campaigns.bastion')), landPick),
    h('div.field', {}, h('span.lbl', {}, t('campaigns.depth')), h('span.small', {}, t('campaigns.depth.hint')), depthPick),
  );
  const create = h('button.btn', { 'data-test': 'create-campaign' }, t('campaigns.create')) as HTMLButtonElement;
  // The hours may be picked in any order; repeated or too close together, the campaign can't be created.
  const hours = (): number[] => [h1, h2, h3].map((s) => Number(s.value)).sort((a, b) => a - b);
  const checkHours = (): void => {
    const ok = waveHoursValid(hours());
    hoursBad.hidden = ok;
    create.disabled = !ok;
  };
  for (const s of [h1, h2, h3]) s.addEventListener('change', checkHours);
  const back = modal(t('campaigns.new'), '', body, [h('button.btn.ghost', { onclick: () => back.remove() }, t('menu.back')), create]);
  create.addEventListener('click', async () => {
    create.disabled = true;
    try {
      const res = await api<{ id: string }>('/api/campaigns', {
        body: {
          name: name.value.trim() || 'Somnhold',
          displayName: display.value.trim() || me.user.displayName || 'Guardián',
          heroClass: cls,
          timezone: tz.value,
          waveHours: hours(),
          depth: depth.value,
          lang: getLang(),
          ...(land.value ? { land: true } : {}),
        },
      });
      back.remove();
      onCreated(res.id);
    } catch {
      toastGlobal(t('net.error'));
      create.disabled = false;
    }
  });
};

export const inviteModal = async (invite: () => Promise<{ url: string }>): Promise<void> => {
  let url = '';
  try {
    url = (await invite()).url;
  } catch {
    toastGlobal(t('net.error'));
    return;
  }
  const field = h('input.text-input', { type: 'text', readonly: true, value: url, 'data-test': 'invite-url' }) as HTMLInputElement;
  const actions: HTMLElement[] = [h('button.btn.ghost', { onclick: () => back.remove() }, t('hud.close'))];
  if (navigator.share) actions.push(h('button.btn.stone', { onclick: () => void navigator.share({ title: 'Somnhold', url }).catch(() => undefined) }, t('campaigns.share')));
  actions.push(
    h('button.btn', {
      onclick: async () => {
        try {
          await navigator.clipboard.writeText(url);
        } catch {
          field.select();
          document.execCommand?.('copy');
        }
        toastGlobal(t('campaigns.copied'));
      },
    }, t('campaigns.copy')),
  );
  const back = modal(t('campaigns.invite'), t('campaigns.inviteHint'), field, actions);
  field.addEventListener('focus', () => field.select());
};

export const acceptInviteModal = async (r: THREE.WebGLRenderer | null, me: Me, code: string, onJoined: (id: string) => void): Promise<void> => {
  let info: { campaignId: string; name: string; players: { name: string; cls: string }[]; member: boolean };
  try {
    info = await api(`/api/invites/${code}`);
  } catch {
    modal(t('invite.invalid'), '', h('div'), [h('button.btn', { onclick: (e: Event) => (e.target as HTMLElement).closest('.modal-back')?.remove() }, t('hud.close'))]);
    return;
  }
  if (info.member) {
    onJoined(info.campaignId);
    return;
  }
  const taken = info.players.map((p) => p.cls);
  let cls: HeroClass = (PLAYABLE_CLASSES.find((c) => !taken.includes(c)) ?? 'warrior') as HeroClass;
  const display = h('input.text-input', { type: 'text', maxlength: 24, value: me.user.displayName }) as HTMLInputElement;
  const crew = h('p.hint', { style: 'margin:0;color:var(--text-soft);font-family:var(--font-serif)' }, info.players.map((p) => `${p.name} (${t(`class.${p.cls}`)})`).join(' · '));
  const body = h(
    'div',
    { style: 'display:grid;gap:12px' },
    crew,
    h('div.field', {}, h('label', {}, t('menu.yourName')), display),
    h('div.field', {}, h('span.lbl', {}, t('menu.pickClass')), classPicker(r, cls, (c) => (cls = c), taken)),
  );
  const join = h('button.btn', { 'data-test': 'join' }, t('invite.join')) as HTMLButtonElement;
  const back = modal(t('invite.title', { name: info.name }), t('invite.hint'), body, [h('button.btn.ghost', { onclick: () => back.remove() }, t('menu.back')), join]);
  join.addEventListener('click', async () => {
    join.disabled = true;
    try {
      const res = await api<{ id: string }>(`/api/invites/${code}/accept`, { body: { heroClass: cls, displayName: display.value.trim() || 'Guardián' } });
      back.remove();
      onJoined(res.id);
    } catch {
      toastGlobal(t('net.error'));
      join.disabled = false;
    }
  });
};

export const signOut = async (): Promise<void> => {
  await api('/api/auth/logout', { body: {} }).catch(() => undefined);
};
